// The Forecasts panel's heading: its name and a search button; searching, a box that filters the
// list to forecasts whose titles hold every typed word (hooks/useForecastSearch.js). Pure props.
import React, { useEffect, useRef } from "react";
import { C } from "../lib/colors.js";
import { ui } from "../lib/telemetry/instrument.js";
import { RailPanelHeading } from "./NavRail.jsx";
import { IconButton } from "./ui/Primitives.jsx";
import { IconClose, IconSearch } from "./ui/Icons.jsx";

// A 32px button inside the heading's 20px line, without growing it.
const BUTTON_FIT = { margin: "-6px -8px -6px 0" };

/**
 * @param {object} props
 * @param {string} props.label the panel's name
 * @param {ReturnType<import("../hooks/useForecastSearch.js").useForecastSearch>} props.search
 */
export default function ForecastsHeading({ label, search }) {
  const inputRef = useRef(null);
  useEffect(() => {
    if (search.open) inputRef.current?.focus();
  }, [search.open]);

  if (!search.open) {
    return (
      <RailPanelHeading
        label={label}
        actions={
          <IconButton
            label="Search forecasts"
            {...ui("forecast-manager.search-open")}
            onClick={search.openSearch}
            style={BUTTON_FIT}
          >
            <IconSearch size={16} />
          </IconButton>
        }
      />
    );
  }
  return (
    <RailPanelHeading
      actions={
        <IconButton
          label="Close search"
          {...ui("forecast-manager.search-close")}
          onClick={search.close}
          style={BUTTON_FIT}
        >
          <IconClose size={16} />
        </IconButton>
      }
    >
      <span aria-hidden="true" style={{ display: "flex", flex: "none", color: C.gray }}>
        <IconSearch size={16} />
      </span>
      <input
        ref={inputRef}
        {...ui("forecast-manager.search-input")}
        data-telemetry-redact=""
        // A text box with the search role: type="search" adds the browser's own clear button
        // beside the heading's close button.
        type="text"
        role="searchbox"
        aria-label="Search forecast titles"
        placeholder="Search forecast titles"
        value={search.query}
        onChange={(e) => search.setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            search.close();
          }
        }}
        style={{
          flex: 1,
          minWidth: 0,
          padding: 0,
          border: "none",
          outline: "none",
          background: "transparent",
          color: C.white,
          font: "inherit",
          fontSize: 14,
          fontWeight: 400,
          lineHeight: "20px",
        }}
      />
    </RailPanelHeading>
  );
}
